import { useState } from "react";
import { Menu, X } from "lucide-react";
import { LoadingText } from "../components/ui";
import { useAuth } from "../hooks/useAuth";
import { useAdminPageData, useAdminTabs } from "../components/Admin/hooks";
import UserTable from "../components/Admin/UserTable";
import TrendingQuestions from "../components/Admin/TrendingQuestions";
import UsageAnalytics from "../components/Admin/UsageAnalytics";
import AppSettings from "../components/Admin/AppSettings";
import FeedbackInbox from "../components/Admin/FeedbackInbox";
import AuditLog from "../components/Admin/AuditLog";
import ManagementView from "../components/Admin/ManagementView";
import Integrations from "../components/Admin/Integrations";
import StatusSettings from "../components/Admin/StatusSettings";
import PermissionsSettings from "../components/Admin/PermissionsSettings";
import TeamsSettings from "../components/Admin/TeamsSettings";
import ZendeskSettings from "../components/Admin/ZendeskSettings";
import SentrySettings from "../components/Admin/SentrySettings";
import TenantsSettings from "../components/Admin/TenantsSettings";
import { ROLES } from "../constants/roles";
import { ADMIN_TAB_GROUPS } from "../components/Admin/types";
import type { AdminTab } from "../components/Admin/types";

function tabLabel(tab: AdminTab, feedbackOpenCount: number) {
  return tab === "Feedback" && feedbackOpenCount > 0
    ? `Feedback (${feedbackOpenCount})`
    : tab;
}

export default function AdminPage() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role?.name === ROLES.SUPER_ADMIN;
  const { tabs, activeTab, setActiveTab } = useAdminTabs(isSuperAdmin);
  const {
    users,
    feedbackOpenCount,
    loading,
    loadUsers,
    handleFeedbackOpenCountChange,
  } = useAdminPageData();

  const [menuOpen, setMenuOpen] = useState(false);

  function selectTab(tab: AdminTab) {
    setActiveTab(tab);
    setMenuOpen(false);
  }

  function renderTab(tab: AdminTab) {
    return (
      <button
        key={tab}
        type="button"
        onClick={() => selectTab(tab)}
        className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          activeTab === tab
            ? "bg-[#1D345F]/10 text-[#1D345F]"
            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        }`}
      >
        {tabLabel(tab, feedbackOpenCount)}
      </button>
    );
  }

  function renderGroups() {
    return ADMIN_TAB_GROUPS.map((group) => {
      const visible = group.tabs.filter((tab) => tabs.includes(tab));
      if (visible.length === 0) return null;
      return (
        <div key={group.label} className="mb-3">
          <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            {group.label}
          </p>
          <div className="flex flex-col gap-0.5">{visible.map(renderTab)}</div>
        </div>
      );
    });
  }

  return (
    <div className="flex h-full">
      {/* Desktop vertical nav */}
      <aside className="hidden md:flex md:flex-col w-52 shrink-0 h-full overflow-y-auto border-r border-gray-200 bg-white py-6 px-3">
        <nav>{renderGroups()}</nav>
      </aside>

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Mobile header bar */}
        <div className="md:hidden flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3 shrink-0">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100"
            aria-label="Toggle admin menu"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <span className="text-sm font-medium text-gray-800">
            {tabLabel(activeTab, feedbackOpenCount)}
          </span>
        </div>

        {/* Mobile dropdown nav */}
        {menuOpen && (
          <div className="md:hidden border-b border-gray-200 bg-white px-3 pb-3 pt-2 shrink-0">
            <nav>{renderGroups()}</nav>
          </div>
        )}

        {/* Page content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          {activeTab === "Questions" ? (
            <TrendingQuestions />
          ) : activeTab === "Analytics" ? (
            <UsageAnalytics />
          ) : activeTab === "Feedback" ? (
            <FeedbackInbox onOpenCountChange={handleFeedbackOpenCountChange} />
          ) : activeTab === "Audit" ? (
            <AuditLog />
          ) : activeTab === "Management" ? (
            <ManagementView />
          ) : activeTab === "Integrations" ? (
            <Integrations />
          ) : activeTab === "Zendesk" ? (
            <ZendeskSettings />
          ) : activeTab === "Sentry" && isSuperAdmin ? (
            <SentrySettings />
          ) : activeTab === "Status" ? (
            <StatusSettings />
          ) : activeTab === "Permissions" ? (
            <PermissionsSettings />
          ) : activeTab === "Teams" ? (
            <TeamsSettings users={users} />
          ) : activeTab === "Settings" ? (
            <AppSettings />
          ) : activeTab === "Tenants" && isSuperAdmin ? (
            <TenantsSettings />
          ) : loading ? (
            <LoadingText>Loading...</LoadingText>
          ) : (
            <>
              {activeTab === "Users" && (
                <UserTable users={users} onRefresh={loadUsers} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
